/**
 * Helpers for reasoning about a problem's command-center (EOC Bridge) incident linkage.
 *
 * US2031585 scope: the backend leaves `problem.incidentNumber` empty when there is no
 * EOC Bridge incident linked to a problem. The UI must expose that "no command-center
 * incident linked" state explicitly (rather than hiding the field or showing a bare "—").
 */

/**
 * Explicit, user-facing message shown where the command-center incident number would
 * appear when a real PRB has no acceptable command-center incident linked. Since
 * US2077729 command-center is a configurable list of accepted groups (not only EOC
 * Bridge), so the copy is group-neutral.
 */
export const NO_COMMAND_CENTER_INCIDENT_MESSAGE = 'No incident linked in an appropriate assignment group';

/**
 * isRealPRB reports whether the given problem number identifies a real, persisted PRB
 * (ServiceNow problem record) rather than a temporary placeholder.
 *
 * A real PRB number starts with "PRB" but is NOT a temporary "PRBT" number. Temporary
 * problems (PRBT...) have not yet been promoted to a ServiceNow problem record, so the
 * "no command-center incident linked" state is not meaningful for them.
 *
 * @param {string} [problemNumber] - The problem number (e.g. "PRB0012345" or "PRBT12345").
 * @returns {boolean} true when the number is a real PRB (not temporary).
 */
export function isRealPRB(problemNumber) {
  if (typeof problemNumber !== 'string') {
    return false;
  }
  const trimmed = problemNumber.trim().toUpperCase();
  return trimmed.startsWith('PRB') && !trimmed.startsWith('PRBT');
}

/**
 * isPermanentTargetNumber reports whether the given number is a permanent ticket
 * identifier that a temporary (PRBT) problem may be promoted to: a real PRB (not the
 * temporary PRBT form) or an EOC Bridge incident (INC).
 *
 * Frontend twin of the backend's `types.IsPermanentTargetNumber`. Like the Go helper
 * this is a routing predicate, not a validator — it classifies the prefix only and
 * does not check the number's shape. (US2097708)
 *
 * @param {string} [problemNumber] - The target number (e.g. "PRB0012345", "INC4862260").
 * @returns {boolean} true when the number is a permanent promotion target.
 */
export function isPermanentTargetNumber(problemNumber) {
  if (typeof problemNumber !== 'string') {
    return false;
  }
  const trimmed = problemNumber.trim().toUpperCase();
  if (trimmed.startsWith('PRB')) {
    return !trimmed.startsWith('PRBT');
  }
  return trimmed.startsWith('INC');
}

/**
 * shouldForwardToPermanent reports whether a LOADED problem record should forward the
 * operator to its permanent (promoted-to) record. Single source of truth for both
 * US2097708 forwards: the Viewer's redirect effect (Viewer.js) and the edit page's
 * load-time forward (ProblemForm.js fetchProblemDetails).
 *
 * Forwarding is gated on `temporary`, which MATCHES AC5 as corrected in Rally on
 * 2026-08-31 ("forward only WHILE temporary"). Historical note only: the AC previously
 * read the opposite, so earlier revisions of this comment described the predicate as a
 * deliberate divergence. It is not one any more — the code is on spec. Do not "fix" it by
 * flipping the polarity; that reintroduces the ping-pong described below.
 *
 * Why forwarding-while-temporary is the right rule: the conversion never clears
 * `Temporary` on the OUTGOING record (engine.go sets Temporary=false on the NEW record
 * only), so a promoted-away, archived PRBT is persisted with temporary:true AND
 * permanentNumber set (confirmed on archive/PRBT505100.json). Gating on `temporary`
 * therefore:
 *   - forwards a promoted-away PRBT to its permanent record (the intended case) — this is
 *     what preserves "search the old PRBT, land on the INC";
 *   - does NOT forward a genuinely permanent record carrying a stale permanentNumber
 *     (temporary:false), which is what kills the permanent<->permanent redirect
 *     ping-pong seen in prod on the double-mint pair;
 *   - does NOT forward a live, never-promoted PRBT (permanentNumber is empty).
 *
 * @param {string} [permanentNumber] - The record's promoted-to number (`permanentNumber`).
 * @param {{number?: string, temporary?: boolean}} [problem] - The loaded problem record.
 * @returns {boolean} true when the operator should be forwarded to permanentNumber.
 */
export function shouldForwardToPermanent(permanentNumber, problem) {
  return Boolean(
    permanentNumber && problem && problem.temporary && problem.number !== permanentNumber,
  );
}

/**
 * shouldShowNoIncidentLinked reports whether the explicit "no command-center incident
 * linked" state should be displayed. This is true only when the incident number is
 * empty AND the problem is a real PRB (not a temporary PRBT).
 *
 * @param {string} [incidentNumber] - The ServiceNow INC number (empty when none linked).
 * @param {string} [problemNumber]  - The problem number.
 * @returns {boolean} true when the explicit no-incident message should be shown.
 */
export function shouldShowNoIncidentLinked(incidentNumber, problemNumber) {
  const hasIncident = typeof incidentNumber === 'string' && incidentNumber.trim() !== '';
  return !hasIncident && isRealPRB(problemNumber);
}

/**
 * resolveIncidentNumberDisplay returns the string that should be shown in the
 * command-center Incident Number field: the incident number when present, the
 * explicit "no command-center incident linked" message when that state applies,
 * or an empty string otherwise.
 *
 * @param {string} [incidentNumber] - The ServiceNow INC number (empty when none linked).
 * @param {string} [problemNumber]  - The problem number.
 * @returns {string} The value to display for the incident number field.
 */
export function resolveIncidentNumberDisplay(incidentNumber, problemNumber) {
  if (incidentNumber) {
    return incidentNumber;
  }
  if (shouldShowNoIncidentLinked(incidentNumber, problemNumber)) {
    return NO_COMMAND_CENTER_INCIDENT_MESSAGE;
  }
  return '';
}

/**
 * hasIncidentLinkageContext reports whether there is any command-center incident
 * linkage context to render — either a real incident number exists, or the explicit
 * "no command-center incident linked" state applies. This is the single source of
 * truth for gating both the Incident Number row and its paired AWS Case row, so the
 * AWS Case value never renders orphaned from its incident context.
 *
 * @param {string} [incidentNumber] - The ServiceNow INC number (empty when none linked).
 * @param {string} [problemNumber]  - The problem number.
 * @returns {boolean} true when incident-linkage context should be rendered.
 */
export function hasIncidentLinkageContext(incidentNumber, problemNumber) {
  return !!(incidentNumber || shouldShowNoIncidentLinked(incidentNumber, problemNumber));
}

/**
 * Placeholder shown in the command-center Incident Number cell when there is no
 * incident value to display but a paired AWS case is present — the incident cell
 * must still render beside the AWS case so the two read as a pair.
 */
export const INCIDENT_NUMBER_PLACEHOLDER = '—';

/**
 * resolveIncidentDisplayWithPlaceholder returns the string to show in the
 * command-center Incident Number field, falling back to a placeholder em-dash
 * only when a paired AWS case is present.
 *
 * It first defers to resolveIncidentNumberDisplay (incident number → explicit
 * "no command-center incident linked" message → empty string). When that yields
 * an empty string, the placeholder is returned only if an AWS case is present, so
 * the incident cell renders alongside its AWS case; otherwise the field stays blank.
 *
 * @param {string}  [incidentNumber] - The ServiceNow INC number (empty when none linked).
 * @param {string}  [problemNumber]  - The problem number.
 * @param {boolean} [awsCasePresent] - Whether a paired AWS case is present.
 * @returns {string} The value to display for the incident number field.
 */
export function resolveIncidentDisplayWithPlaceholder(incidentNumber, problemNumber, awsCasePresent) {
  const resolved = resolveIncidentNumberDisplay(incidentNumber, problemNumber);
  if (resolved) {
    return resolved;
  }
  return awsCasePresent ? INCIDENT_NUMBER_PLACEHOLDER : '';
}
